<script lang="ts" context="module">
  import { t } from "../../../core";
  export interface ISchedule {
    rows: IScheduleRow[];
    currentWeekIndex: number;
    deadlineWeekIndex: number | null;
    vacationWeekIndexes: number[];
    years: IScheduleYear[];
    weeks: IScheduleWeek[];
  }

  export interface IScheduleRow {
    name: string;
    url: string;
    isCurrent: boolean;
    isFinished: boolean;
    realWeekIndexes: number[];
    expectedWeekIndexes: number[];
    delayWeekIndexes: number[];
    comment?: string | null;
  }

  export interface IScheduleYear {
    year: number;
    startWeekIndex: number;
  }

  export interface IScheduleWeek {
    index: number;
    number: number;
  }
</script>

<script>
  import axios, { AxiosResponse } from "axios";

  import { md } from "../../../core";
  import DataCell from "./DataCell.svelte";
  import Legend from "./legend/Legend.svelte";

  const isMobile = window.innerWidth < md;
  const FIRST_COLUMN_WIDTH = isMobile ? 200 : 300;
  const dataPromise: Promise<AxiosResponse<ISchedule>> =
    axios.get("/api/Schedule");

  let currentScroll = 0;

  $: getYearOffset = (year: IScheduleYear, firstYear: IScheduleYear) => {
    const absOffset =
      (year.startWeekIndex - firstYear.startWeekIndex) * 40 +
      FIRST_COLUMN_WIDTH;
    return currentScroll > absOffset && currentScroll > FIRST_COLUMN_WIDTH
      ? currentScroll
      : absOffset;
  };

  const scrollHandler = (e: UIEvent) =>
    (currentScroll = (e.target as Element).scrollLeft);

  function initScrollPosition(node: HTMLDivElement, data: ISchedule) {
    const OFFSET_FOR_VIEW = isMobile ? 0 : 5;
    node.scrollLeft =
      (data.currentWeekIndex - data.weeks[0].index - OFFSET_FOR_VIEW) * 40;
  }
</script>

{#await dataPromise then { data }}
  {#if data}
    <Legend />
    <div class="schedule-wrapper" style="--column-with: {FIRST_COLUMN_WIDTH}px">
      <div
        class="schedule"
        on:scroll={scrollHandler}
        use:initScrollPosition={data}
      >
        <div class="flex-row">
          <div class="first-column years-title">{t("General.Year.Title")}</div>

          {#each data.years as year}
            <div
              class="year"
              style="transform: translateX( {getYearOffset(
                year,
                data.years[0]
              )}px);"
            >
              {year.year}
            </div>
          {/each}
        </div>
        <div class="flex-row">
          <div class="first-column weeks-title">{t("General.Week.Title")}</div>
          {#each data.weeks as week}
            <div
              class="week"
              class:active={data.currentWeekIndex === week.index}
              class:vacation={data.vacationWeekIndexes.includes(week.index)}
            >
              {week.number}
            </div>
          {/each}
        </div>

        {#each data.rows as row}
          <div
            class="flex-row"
            style="width: {data.weeks.length * 40 + FIRST_COLUMN_WIDTH}px"
          >
            <div class="first-column data-title">
              <div class="data-title-icon">
                {#if row.isCurrent}
                  <svg class="icon" width="16" height="16" aria-label="current">
                    <use xlink:href="#current" />
                  </svg>
                {/if}
                {#if row.isFinished}
                  <svg class="icon" width="14" height="10" aria-label="check">
                    <use xlink:href="#check" />
                  </svg>
                {/if}
              </div>

              <span class="first-column__title">
                {row.name}

                {#if row.url}
                  <a class="link first-column__link" href={row.url}>
                    {t("General.Schedule.ReadMoreLink")}
                  </a>
                {/if}
              </span>
            </div>

            {#each data.weeks as week}
              <DataCell
                currentWeekIndex={data.currentWeekIndex}
                deadlineWeekIndex={data.deadlineWeekIndex}
                {week}
                {row}
              />
            {/each}
          </div>
        {/each}
      </div>
    </div>
  {:else}
    <p class="no-data-label">{t("General.Schedule.NoData")}</p>
  {/if}
{/await}

<style lang="scss">
  .schedule-wrapper {
    display: grid;
  }
  .schedule {
    width: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    background-color: $text-reverse-color;
    font-weight: 600;
  }
  .first-column {
    padding: 12px 15px;
    flex: 0 0 var(--column-with);
    max-width: var(--column-with);
    min-width: var(--column-with);

    &__link {
      @include small;
      display: inline-block;
      margin-left: 4px;
      font-weight: 400;

      &:visited {
        color: $key-color;
      }
    }
  }
  .flex-row {
    display: flex;
    position: relative;
  }
  .weeks-title {
    border-top: 1px solid #a2a2a2;
    border-bottom: 1px solid #a2a2a2;
  }
  .week {
    min-width: 40px;
    padding: 12px 10px;
    border-top: 1px solid #a2a2a2;
    border-left: 1px solid #dedede;
    border-bottom: 1px solid #a2a2a2;
    font-weight: 400;

    &.vacation {
      @include stripes-pattern;
    }

    &.active {
      background: #db8700;
      color: #fff;
      border-left-color: #db8700;
    }

    &.active.vacation {
      @include stripes-active-pattern;
    }
  }

  .year {
    position: absolute;
    padding: 12px 10px;
    border-left: 1px solid #dedede;
    background: #fff;
    transition-duration: 0.1s;
    will-change: transform;
  }
  .data-title {
    border-bottom: 1px solid #a2a2a2;
    display: flex;
    background-color: $text-reverse-color;

    @include md {
      position: sticky;
      left: 0;
      z-index: 3;
    }
  }
  .data-title-icon {
    min-width: 25px;
  }
</style>
